import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    HostListener,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { RouterService } from '@services/router.service';
import { DocumentService } from '@services/api/document/document.service';
import { RevisionDataService } from '@services/data/revision-data-service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { ResizableDirective } from '@directives/resizable.directive';
import { TabsComponent } from '@shared/tabs/tabs.component';
import { PanelComponent } from '@shared/panel/panel.component';
import { DiscrepanciesTabComponent } from '@main/discrepancies-tab/discrepancies-tab.component';
import { InternalsTabComponent } from '@main/internals-tab/internals-tab.component';
import { HeaderDocumentDetailsComponent } from '@main/header-document-details/header-document-details.component';
import { DocumentMetadataTabComponent } from './document-metadata-tab/document-metadata-tab.component';
import { DocumentRevisionsComponent } from './document-revisions/document-revisions.component';
import { WorkflowDocument } from '@models/document';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { DocumentDetailTab, DocumentDetailTabs } from '@main/utils';
import { Role } from '@models/user';
import {
    selectSelectedDiscrepancy,
    selectSelectedTab,
} from 'app/store/selectors/document.selectors';
import { saveSummaryPanelData } from 'app/store/actions/document.actions';
import DATA_QA from '@automation/data-qa.json';
import { take } from 'rxjs';

@Component({
    selector: 'app-summary-panel',
    templateUrl: './summary-panel.component.html',
    styleUrl: './summary-panel.component.scss',
    imports: [
        TabsComponent,
        DiscrepanciesTabComponent,
        DocumentMetadataTabComponent,
        InternalsTabComponent,
        ResizableDirective,
        PanelComponent,
        HeaderDocumentDetailsComponent,
        DocumentRevisionsComponent,
    ],
})
export class SummaryPanelComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) documentId!: number;
    @Output() panelClose = new EventEmitter();

    @ViewChild(PanelComponent, { read: ElementRef }) panelRef!: ElementRef;

    activeTab = DocumentDetailTab.DISCREPANCIES;
    document: WorkflowDocument | null = null;
    initialSelectedTabIndex: number | null = null;
    tabsData = DocumentDetailTabs;
    canEditDocument: boolean = false;
    readonly tabs: typeof DocumentDetailTab = DocumentDetailTab;
    readonly dataQa = DATA_QA;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    private _mouseDownInsidePanel = false;

    private readonly store = inject(Store);
    private readonly routerService = inject(RouterService);
    private readonly documentService = inject(DocumentService);
    private readonly revisionDataService = inject(RevisionDataService);
    private readonly userService = inject(UserService);
    private readonly userDataService = inject(UserDataService);

    ngOnInit() {
        this.fetchDocumentDetails(this.documentId);
        this.isDocumentEditable(this.documentId);
        this.initializeFilteredTabs();
        this.setSelectedTab();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const documentIdChange = changes['documentId'];
        if (documentIdChange && !documentIdChange.firstChange) {
            this.fetchDocumentDetails(documentIdChange.currentValue);
            this.isDocumentEditable(documentIdChange.currentValue);
            this.setSelectedTab();
        }
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
    }

    onRevisionClicked(revision: number) {
        this.updateSelectedRevisionId(revision);
        this.goToDetails(revision);
    }

    goToDetails(revisionId?: number) {
        this.store
            .select(selectSelectedDiscrepancy)
            .pipe(take(1))
            .subscribe((disc) => {
                this.routerService.goDocumentDetails(
                    this.documentId,
                    this.activeTab,
                    disc ?? undefined,
                    revisionId,
                );
            });
    }

    closePanel() {
        this.panelClose.emit();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMouseDown(event: MouseEvent) {
        const panelEl: HTMLElement | undefined = this.panelRef?.nativeElement;
        const target = event.target as HTMLElement;
        this._mouseDownInsidePanel = !!panelEl && panelEl.contains(target);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const panelEl: HTMLElement | undefined = this.panelRef?.nativeElement;
        if (!panelEl) return; // Panel not initialized yet

        const target = event.target as HTMLElement;

        // Ignore clicks on CDK overlay (mat-menu, mat-dialog, etc.)
        if (target.closest('.cdk-overlay-container')) {
            return;
        }

        // Interactions started inside the panel (resize drags, text selection)
        // can end outside of it and report an outside target
        const startedInsidePanel = this._mouseDownInsidePanel;
        this._mouseDownInsidePanel = false;
        if (startedInsidePanel) {
            return;
        }

        if (!panelEl.contains(target)) {
            this.closePanel();
        }
    }

    onTabSelected(index: number) {
        this.activeTab = this.tabsData[index].id;
        this.store.dispatch(
            saveSummaryPanelData({
                selectedDocument: this.documentId,
                selectedTab: this.activeTab,
                selectedDiscrepancy: null,
            }),
        );
    }

    private updateSelectedRevisionId(id: number) {
        if (id) this.revisionDataService.updateSelectedRevisionId(id);
    }

    private fetchDocumentDetails(documentId: number) {
        this._subscriptions['doc-details'] = this.documentService
            .getDocumentDetails(documentId)
            .subscribe({
                next: (res) => (this.document = res),
            });
    }

    private setSelectedTab() {
        const selectedTab$ = this.store.select(selectSelectedTab);

        this._subscriptions['selected-tab'] = selectedTab$.subscribe({
            next: (tab) => {
                if (tab) {
                    this.activeTab = tab;
                    this.initialSelectedTabIndex = this.tabsData.findIndex(
                        (item) => item.id === tab,
                    );
                }
            },
        });
    }

    private isDocumentEditable(documentId: number) {
        this.canEditDocument = false;

        this._subscriptions['can-edit-document'] = this.documentService
            .canEditDocument(documentId)
            .subscribe({
                next: (canEdit) => {
                    if (canEdit) {
                        this.canEditDocument = true;
                        return;
                    }

                    this.allowCollaboratorDetailAccess(documentId);
                },
            });
    }

    private allowCollaboratorDetailAccess(documentId: number) {
        const currentUserId = this.userDataService.currentUser?.id;

        if (!currentUserId) {
            this.canEditDocument = false;
            return;
        }

        this._subscriptions['collaborator-detail-access'] = this.documentService
            .getCollaborators(documentId)
            .subscribe({
                next: ({ collaborators }) => {
                    this.canEditDocument = collaborators.some(
                        (collaborator) => collaborator.id === currentUserId,
                    );
                },
                error: () => {
                    this.canEditDocument = false;
                },
            });
    }

    private initializeFilteredTabs() {
        this._subscriptions['user-for-tabs'] = this.userService
            .getUser()
            .subscribe({
                next: (user) => {
                    if (user && user.role !== null) {
                        // Only Host Admin can see WF Internals tab
                        if (user.role === Role.Host_Admin) {
                            this.tabsData = DocumentDetailTabs;
                        } else {
                            // Filter out WF_INTERNALS for non-Host Admin users
                            this.tabsData = DocumentDetailTabs.filter(
                                (tab) =>
                                    tab.id !== DocumentDetailTab.WF_INTERNALS,
                            );
                        }
                    }
                },
            });
    }
}
